import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'

/**
 * Documentele campaniei, la o adresă proprie: `/s/<cod>/reguli`.
 *
 * Ghidul intern cere ca regulamentul să fie „disponibil gratuit" la un URL public, menționat pe
 * material. Cine vede afișul trebuie să poată citi regulile fără să se înscrie și fără bilet.
 *
 * De când formularul trimite aici cu linkuri — în loc să afișeze textele în pagină, ca la modelul
 * Nestlé pe care l-a arătat Alexandru — pagina asta e ȘI stratul al doilea al informării. Deci
 * are câte o secțiune pentru fiecare document, cu ancoră proprie:
 *
 *   #regulament  — regulamentul campaniei (temeiul participării)
 *   #informare   — informarea art. 13 (transparența asupra datelor)
 *   #marketing   — comunicările comerciale
 *
 * Sunt documente diferite, cu temeiuri diferite, și formularul cere bife separate pentru ele.
 * Sub un singur titlu, cum erau înainte, niciuna nu se putea dovedi singură.
 *
 * Pagina nu cere nimic și nu colectează nimic — nici măcar un clic. E singurul ecran din tot
 * modulul care doar arată.
 */

interface Text {
  continut: string
  versiune: string
}

interface Reguli {
  denumire: string
  locatie: string | null
  regulament: Text | null
  informare: Text | null
  marketing: Text | null
  /** Regulamentul pe site-ul firmei. Când există, el e cel valabil, nu textul din CRM. */
  urlRegulament: string | null
}

export function ReguliPage() {
  const { slug = '' } = useParams()
  const [date, setDate] = useState<Reguli | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)

  useEffect(() => {
    let anulat = false
    axios
      .get<Reguli>(`/api/public/sampling/${encodeURIComponent(slug)}/reguli`)
      .then((r) => {
        if (!anulat) setDate(r.data)
      })
      .catch(() => {
        if (!anulat) setEroare('Nu am găsit documentele acestei campanii.')
      })
    return () => {
      anulat = true
    }
  }, [slug])

  /*
    Ancora din adresă, respectată la încărcare. Formularul trimite direct la `#informare`, iar
    browserul nu poate sări acolo singur: secțiunea nu există încă în clipa în care se deschide
    pagina, textele vin de la server.
  */
  useEffect(() => {
    if (!date) return
    const id = window.location.hash.replace('#', '')
    if (!id) return
    document.getElementById(id)?.scrollIntoView({ behavior: 'auto', block: 'start' })
  }, [date])

  return (
    <div className="min-h-screen bg-b4l-bg px-4 py-8">
      <div className="mx-auto max-w-2xl">
        {eroare && <p className="text-sm text-b4l-red">{eroare}</p>}
        {!date && !eroare && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}

        {date && (
          <>
            <h1 className="text-2xl font-semibold text-b4l-text">{date.denumire}</h1>
            {date.locatie && <p className="text-sm text-b4l-text-muted">{date.locatie}</p>}

            {/*
              Cuprins. Pagina ține acum trei documente, iar unul singur dintre ele poate fi lung
              cât un regulament notarial — fără cuprins, cine vine după informare derulează
              printr-un contract întreg ca s-o găsească.
            */}
            <nav className="mt-4 flex flex-wrap gap-2">
              {(date.regulament || date.urlRegulament) && (
                <a
                  href="#regulament"
                  className="rounded-full border border-b4l-border bg-white px-3 py-1 text-xs text-b4l-text"
                >
                  Regulamentul campaniei
                </a>
              )}
              {date.informare && (
                <a
                  href="#informare"
                  className="rounded-full border border-b4l-border bg-white px-3 py-1 text-xs text-b4l-text"
                >
                  Informare privind datele
                </a>
              )}
              {date.marketing && (
                <a
                  href="#marketing"
                  className="rounded-full border border-b4l-border bg-white px-3 py-1 text-xs text-b4l-text"
                >
                  Comunicări comerciale
                </a>
              )}
            </nav>

            {/*
              Cand regulamentul traieste pe site-ul firmei, pagina nu-l copiaza: trimite acolo.
              Textul autentificat notarial are un singur original, iar o copie tinuta aici s-ar
              despari de el la prima corectura — si tocmai copia ar fi cea citita de participanti.
            */}
            {date.urlRegulament ? (
              <section id="regulament" className="mt-8">
                <h2 className="text-lg font-semibold text-b4l-text">Regulamentul campaniei</h2>
                <p className="mt-2 text-sm text-b4l-text-muted">
                  Regulamentul autentificat al campaniei se află pe site-ul Boost4Life.
                </p>
                <a
                  href={date.urlRegulament}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 inline-block rounded-lg bg-b4l-blue px-4 py-2 text-sm font-medium text-white"
                >
                  Vezi regulamentul ↗
                </a>
              </section>
            ) : (
              <Sectiune
                id="regulament"
                titlu="Regulamentul campaniei"
                text={date.regulament}
                lipsa="Regulamentul nu este publicat încă. Scrie-ne la contact@boost4life.ro și ți-l trimitem."
              />
            )}
            <Sectiune
              id="informare"
              titlu="Informare privind prelucrarea datelor"
              text={date.informare}
              lipsa="Informarea nu este publicată încă. Scrie-ne la contact@boost4life.ro."
            />
            <Sectiune
              id="marketing"
              titlu="Comunicări comerciale"
              text={date.marketing}
              lipsa={null}
            />

            <p className="mt-6 text-xs text-b4l-text-muted">
              Participarea este gratuită și nu presupune nicio achiziție.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function Sectiune({
  id,
  titlu,
  text,
  lipsa,
}: {
  id: string
  titlu: string
  text: Text | null
  /** Ce se scrie când textul lipsește. `null` = secțiunea nu se afișează deloc. */
  lipsa: string | null
}) {
  if (!text && !lipsa) return null
  return (
    // `scroll-mt-4` lasă loc deasupra când se sare aici cu ancora; altfel titlul stă lipit de
    // marginea de sus și pare tăiat.
    <section id={id} className="mt-6 scroll-mt-4 rounded-lg bg-white p-4">
      {/*
        Fara numarul versiunii pe ecran. Alexandru: „elimina versiunea de peste tot pe unde e
        afisata".

        Nu se pierde nimic din dovada: versiunea se scrie in continuare pe inscriere
        (`textInformareId`) si pe procesul-verbal. Acolo e locul ei — intr-un document care se
        arhiveaza, nu intr-un titlu pe care participantul il citeste inainte sa bifeze. Un „vV2"
        langa un link nu-i spune nimic cui completeaza un formular la un targ.
      */}
      <h2 className="text-base font-semibold text-b4l-text">{titlu}</h2>
      <div className="mt-3 text-sm leading-relaxed text-b4l-text-muted">
        {text ? (
          /*
            Titlurile de sectiune, marcate cu „## " in text, se vad ca titluri si aici — nu ca
            doua diezuri. Textul e acelasi cu cel tiparit pe procesul-verbal, deci marcajul
            trebuie inteles in amandoua locurile.
          */
          text.continut.split(/\n\s*\n/).map((bucata, i) => {
            const t = bucata.trim()
            if (!t) return null
            return t.startsWith('## ') ? (
              <h3 key={i} className="mt-4 font-semibold text-b4l-text">
                {t.slice(3).trim()}
              </h3>
            ) : (
              <p key={i} className="mt-2 whitespace-pre-wrap break-words">
                {adreseClicabile(t)}
              </p>
            )
          })
        ) : (
          <span className="text-b4l-red">{lipsa}</span>
        )}
      </div>
    </section>
  )
}

/**
 * Adresele web din text devin clicabile, dar rămân scrise.
 *
 * Alexandru, arătând documentul Word unde „Politica de confidențialitate Boost4Life" e ancoră:
 * „mă gândeam să fie link-uri acolo". Pe ecran are sens; pe hârtie n-are, iar textul e ACELAȘI
 * cu cel tipărit pe procesul-verbal. Compromisul care ține la amândouă: adresa se vede și se
 * poate apăsa. Nu se înlocuiește cu un nume — doar devine activă.
 */
function adreseClicabile(text: string): (string | JSX.Element)[] {
  const ancora = (url: string, eticheta: string, i: number) => (
    <a key={i} href={url} target="_blank" rel="noreferrer" className="text-b4l-blue underline">
      {eticheta}
    </a>
  )

  // Întâi formele `[nume](adresă)`, apoi adresele rămase. Vezi `pv.template.ts` pentru de ce
  // trăiesc amândouă: textul e același, iar procesul-verbal se și tipărește.
  return text.split(/(\[[^\]]+\]\(https?:\/\/[^\s)]+\)|https?:\/\/[^\s)]+)/g).map((bucata, i) => {
    const m = /^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/.exec(bucata)
    if (m) return ancora(m[2], m[1], i)
    if (/^https?:\/\//.test(bucata)) return ancora(bucata, bucata, i)
    return bucata
  })
}
